import {
  GA_EVENTS,
  isPspAdministrator,
  sendGAEvent,
  useGAPageViews,
} from '@dx-ui/dx-common/src';
import {
  AutocompleteInput,
  CreateButton,
  Datagrid,
  DeleteWithConfirmButton,
  EditButton,
  ExportButton,
  Filter,
  FilterProps,
  FunctionField,
  List,
  ReferenceField,
  ReferenceInput,
  TextField,
  TopToolbar,
  useGetIdentity,
  useNotify,
  usePermissions,
  useTranslate,
} from 'react-admin';
import { Empty } from '../../../common/Empty';

const ProductUsageFilters = (props: Omit<FilterProps, 'children'>) => {
  const { permissions, loaded } = usePermissions();
  if (!loaded || !permissions) return null;

  return (
    <Filter {...props}>
      {isPspAdministrator(permissions) && (
        <ReferenceInput
          label='resources.accounts.list.headers.company'
          source='idaccounts'
          reference='management/company'
          filterToQuery={(searchText) => ({
            accountname: [searchText],
          })}
          sort={{ field: 'accountname', order: 'ASC' }}
          allowEmpty={true}
          alwaysOn
        >
          <AutocompleteInput
            optionText='accountname'
            optionValue='id'
            resettable={true}
          />
        </ReferenceInput>
      )}
      {isPspAdministrator(permissions) && (
        <ReferenceInput
          label={'dxMessages.companyManagement.productUsage.product'}
          source='iddxproduct'
          reference='management/dxproduct'
          filterToQuery={(searchText) => ({
            accountname: [searchText],
          })}
          sort={{ field: 'description', order: 'ASC' }}
          allowEmpty={true}
          alwaysOn
        >
          <AutocompleteInput
            optionText='description'
            optionValue='id'
            options={{ type: 'search' }}
          />
        </ReferenceInput>
      )}
    </Filter>
  );
};

const ProductUsageList = (props) => {
  const notify = useNotify();
  const { permissions, loaded } = usePermissions();
  const translate = useTranslate();
  useGAPageViews();
  const { identity } = useGetIdentity();
  // @ts-ignore
  const account: Account = identity;
  if (!loaded || !permissions) return null;

  const ListActions = () => {
    return (
      <TopToolbar>
        {isPspAdministrator(permissions) && <CreateButton />}
        <ExportButton maxResults={5000} />
      </TopToolbar>
    );
  };

  return (
    <List
      {...props}
      bulkActionButtons={false}
      filters={<ProductUsageFilters />}
      empty={isPspAdministrator(permissions) ? <Empty /> : false}
      actions={<ListActions />}
    >
      <Datagrid style={{ marginTop: '1em' }}>
        {isPspAdministrator(permissions) && (
          <ReferenceField
            label='resources.accounts.list.headers.company'
            source='idaccounts'
            reference='management/company'
            link={false}
            sortable={false}
          >
            <TextField source='accountname' />
          </ReferenceField>
        )}
        <ReferenceField
          label={'dxMessages.companyManagement.productUsage.product'}
          source='iddxproduct'
          reference='management/dxproduct'
          link={false}
          sortable={false}
        >
          <TextField source='description' />
        </ReferenceField>
        <TextField
          label={'dxMessages.companyManagement.productUsage.totalusage'}
          source='totalcount'
          sortable={false}
        />
        <TextField
          label={'dxMessages.companyManagement.productUsage.totalcredit'}
          source='totalcredit'
          sortable={false}
        />
        <FunctionField
          label={'dxMessages.companyManagement.productUsage.advailible'}
          render={(record: any) => {
            return record.totalcredit - record.totalcount;
          }}
          sortable={false}
        />
        <TextField
          label={'dxMessages.companyManagement.productUsage.thresold'}
          source='thresold'
          sortable={false}
        />
        {isPspAdministrator(permissions) && <EditButton />}
        {isPspAdministrator(permissions) && (
          <DeleteWithConfirmButton
            onClick={() =>
              sendGAEvent(
                GA_EVENTS.categories.PRODUCT_USAGE_MGMT.name,
                GA_EVENTS.categories.PRODUCT_USAGE_MGMT.actions
                  .DELETE_PRODUCT_USAGE,
                account?.company?.cmsRootDir
              )
            }
            onFailure={(error) => {
              if (error.status === 400) {
                notify(
                  'dxMessages.companyManagement.supportedProducts.delete_supportedproduct_error',
                  'error',
                  {
                    _: 'Unable to delete the contracted product',
                    error: translate(
                      'dxMessages.companyManagement.supportedProducts.delete_supportedproduct_error'
                    ),
                  }
                );
              } else {
                notify(
                  'dxMessages.companyManagement.supportedProducts.delete_supportedproduct_error',
                  'error',
                  {
                    _: 'Unable to delete the contracted product',
                    error: `${error}`,
                  }
                );
              }
            }}
          />
        )}
      </Datagrid>
    </List>
  );
};

export default ProductUsageList;
